/* @ts-ignore */
import npyjs from 'npyjs'
import { useContext, useEffect, useState } from 'react'

import Stage from './Stage'
import { batch as _batch } from 'react-redux'
import { useAppSelector } from '@/hooks'
import { modelScaleProps } from '@/components/SegmentHelpers/Interfaces'
import SegmentContext from '@/components/SegmentHelpers/hooks/createContext'
import { handleImageScale } from '@/components/SegmentHelpers/scaleHelper'
import { modelData } from '@/components/SegmentHelpers/onnxModelAPI'
import {
  clipImageDataToStick,
  imageToBase64 as _imageToBase64,
  imageToImageData,
  onnxMaskToImage
} from '@/components/SegmentHelpers/maskUtils'
import { CLICKTYPE_ADD, SEGTYPE_CLICK, SEGTYPE_HOVER } from '@/components/SegmentHelpers/config'
import { SEGMENTINFO_URL } from '@/server'

import * as ort from 'onnxruntime-web'
import type { InferenceSession, Tensor as OrtTensor } from 'onnxruntime-web'

const publicAssetUrl = (path: string) => `${import.meta.env.BASE_URL}${path}`

ort.env.wasm.wasmPaths = publicAssetUrl('ort/')

const DisplayArea = () => {
  const imageId = useAppSelector((state) => state.analysisImageInfo.imageId)

  const [model, setModel] = useState<InferenceSession | null>(null) // ONNX model
  const [tensor, setTensor] = useState<OrtTensor | null>(null) // Image embedding tensor
  // The ONNX model expects the input to be rescaled to 1024.
  // The modelScale state variable keeps track of the scale values.
  const [modelScale, setModelScale] = useState<modelScaleProps | null>(null)
  // Initialize the ONNX model. load the image, and load the SAM
  // pre-computed image embedding
  // Define image, embedding and model paths
  const {
    clicks: [clicks],
    image: [image, setImage],
    maskImg: [, setMaskImg],
    clipData: [, setClipData],
    segmentType: [segmentType, setSegmentType],
    resultMask: [resultMask, setResultMask],
    clickType: [, setClickType],
    isLoading: [_isLoading, _setIsLoading],
    stickers: [, setStickers],
    isSaving: [isSaving]
  } = useContext(SegmentContext)!

  const loadImage = async (url: string) => {
    try {
      const img = new Image()

      img.src = url
      img.setAttribute('crossOrigin', 'anonymous')
      img.onload = () => {
        const { height, width, samScale } = handleImageScale(img)
        // dispatch(setImageData(imageToImageData(img)))
        setModelScale({
          height: height, // original image height
          width: width, // original image width
          samScale: samScale // scaling factor for image which has been resized to longest side 1024
        })
        // img.src = imageToBase64(img)
        img.width = width
        img.height = height
        // console.log(img)
        setImage(img)
      }
    } catch (error) {
      console.log(error)
    }
  }

  // Decode a Numpy file into a tensor.
  const loadNpyTensor = async (tensorFile: string, dType: OrtTensor.Type) => {
    const npLoader = new npyjs()
    const npArray = await npLoader.load(tensorFile)
    const tensor = new ort.Tensor(dType, npArray.data, npArray.shape)
    return tensor
  }
  const runONNX = async () => {
    try {
      if (model === null || clicks === null || tensor === null || modelScale === null) return
      else {
        // Preapre the model input in the correct format for SAM.
        // The modelData function is from onnxModelAPI.tsx.
        const feeds = modelData({
          clicks,
          tensor,
          modelScale
        })
        if (feeds === undefined) return
        // console.log("feeds", feeds)
        // Run the SAM ONNX model with the feeds returned from modelData()
        const results = await model.run(feeds)
        // console.log('results', results)
        const output = results[model.outputNames[0]]
        // console.log('output', output)
        // The predicted mask returned from the ONNX model is an array which is
        // rendered as an HTML image using onnxMaskToImage() from maskUtils.tsx.
        // console.log("setmaskimg")
        // dispatch(
        //   setMaskImg(onnxMaskToImage(output.data, output.dims[3], output.dims[2], segmentType))
        // )
        setMaskImg(onnxMaskToImage(output.data, output.dims[3], output.dims[2], segmentType))

        if (image && segmentType == SEGTYPE_CLICK && resultMask) {
          const result = clipImageDataToStick(
            output.data,
            output.dims[3],
            output.dims[2],
            imageToImageData(image).data,
            resultMask
          )
          setClipData({
            ...result,
            crop_box: [output.dims[3], output.dims[2]],
            point_coords: clicks
          })
        }

        // console.log(currentImageData)
      }
    } catch (e) {
      console.log(e)
    }
  }

  // Run the ONNX model every time clicks has changed
  useEffect(() => {
    if (!isSaving && clicks && clicks.length > 0) {
      // console.log("Running ONNX")
      runONNX()
    }
  }, [clicks])

  useEffect(() => {
    // Initialize the ONNX model
    const MODEL_DIR = publicAssetUrl('model/sam_onnx_quantized_example.onnx')
    // const MODEL_DIR="segment/static/sam_onnx_quantized_example.onnx"
    const initModel = async () => {
      try {
        if (MODEL_DIR === undefined) return
        const URL: string = MODEL_DIR
        const model = await ort.InferenceSession.create(URL)
        console.log('initialized model')
        setModel(model)
      } catch (e) {
        console.log(e)
      }
    }
    initModel()
  }, [])
  useEffect(() => {
    // console.log(isSaving)
    if (isSaving || !imageId) return
    // console.log(filename)
    const IMAGE_PATH = `/static/data/picture/${imageId}.png`
    const IMAGE_EMBEDDING = `/static/data/npy/${imageId}.npy`

    // dispatch(setMaskImg(null))
    setMaskImg(null)
    setResultMask(null)
    setStickers([])
    setSegmentType(SEGTYPE_HOVER)
    setClickType(CLICKTYPE_ADD)
    // Load the image
    const url = SEGMENTINFO_URL + IMAGE_PATH
    // console.log(url)
    loadImage(url)
    console.log('load npy')
    // Load the Segment Anything pre-computed embedding
    Promise.resolve(loadNpyTensor(SEGMENTINFO_URL + IMAGE_EMBEDDING, 'float32')).then((embedding) =>
      setTensor(embedding)
    )
  }, [isSaving])

  return <Stage />
}

export default DisplayArea
